
export enum Region {
  USA = 'USA (FDA)',
  EU = 'Europe (EMA)',
  JAPAN = 'Japan (PMDA)',
  CHINA = 'China (NMPA)',
  UK = 'UK (MHRA)',
  CANADA = 'Canada (Health Canada)',
  BRAZIL = 'Brazil (ANVISA)',
  AUSTRALIA = 'Australia (TGA)'
}

export enum TherapeuticArea {
  ONCOLOGY = 'Oncology',
  RARE_DISEASE = 'Rare Disease',
  CNS = 'CNS/Neurology',
  GENE_THERAPY = 'Cell & Gene Therapy',
  CARDIOVASCULAR = 'Cardiovascular',
  IMMUNOLOGY = 'Immunology',
  INFECTIOUS_DISEASE = 'Infectious Disease',
  DIGITAL_HEALTH = 'Digital Therapeutics'
}

export enum ServiceType {
  STRATEGY = 'Regulatory Strategy',
  SUBMISSIONS = 'Submissions (NDA/BLA/MAA)',
  MEDICAL_WRITING = 'Medical Writing',
  PUBLISHING = 'eCTD Publishing',
  INTELLIGENCE = 'Regulatory Intelligence',
  AD_COM = 'Advisory Committee Prep'
}

export enum RegulatoryPathway {
  STD_505_B_1 = '505(b)(1) NDA',
  STD_505_B_2 = '505(b)(2) NDA',
  BLA = 'BLA (Biologics)',
  ANDA = 'ANDA (Generics)',
  BIOSIMILAR = '351(k) Biosimilar',
  ORPHAN = 'Orphan Drug',
  ACCELERATED = 'Accelerated Approval',
  BREAKTHROUGH = 'Breakthrough Therapy',
  RMAT = 'RMAT Designation',
  PRIME = 'EMA PRIME'
}

export enum ProductType {
  SMALL_MOLECULE = 'Small Molecule',
  BIOLOGIC = 'Biologic',
  CELL_GENE = 'Cell & Gene Therapy',
  MED_DEVICE = 'Medical Device / MedTech',
  COMBINATION = 'Combination Product',
  DIGITAL = 'Digital Therapeutic (SaMD)'
}

export enum CompanySize {
  BOUTIQUE = 'Boutique (1-10)',
  SMALL = 'Small (11-50)',
  MID = 'Mid-Size (51-200)',
  LARGE = 'Large (200+)'
}

export enum CompanyStage {
  PRECLINICAL = 'Preclinical',
  PHASE_1 = 'Phase I',
  PHASE_2 = 'Phase II',
  PHASE_3 = 'Phase III',
  COMMERCIAL = 'Commercial',
  SERVICE_PROVIDER = 'CRO / Service Provider'
}

export type UserStatus = 'active' | 'suspended' | 'pending';
export type UserRole = 'admin' | 'consultant' | 'client';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status?: UserStatus;
  isOnboardingComplete: boolean;
  organization?: string;
  lastLogin?: string;
}

export interface Consultancy {
  id: string;
  name: string;
  tagline: string;
  description: string;
  logoUrl: string;
  regions: Region[];
  therapeuticAreas: TherapeuticArea[];
  services: ServiceType[];
  pathways: RegulatoryPathway[];
  productTypes: ProductType[];
  companySize: CompanySize;
  badges: string[];
  verifiedApprovals: number;
  orphanDesignations: number;
  hourlyRateStart?: number;
  hourlyRateEnd?: number;
  teamSize: number;
  foundedYear: number;
  isSponsored?: boolean;
  responseTime: string; // e.g. "24 hours"
  availability: string; // e.g. "Immediate"
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  
  // Deep Profile Data
  teamMembers?: TeamMember[];
  technologies?: string[]; // e.g. "Veeva Vault", "Lorenz docuBridge"
  languages?: string[];
  caseStudies?: CaseStudy[];
}

export interface TeamMember {
    id: string;
    name: string;
    role: string;
    credentials: string[]; // PhD, RAC, Ex-FDA
    photoUrl?: string;
    linkedinUrl?: string;
}

export interface CaseStudy {
    id: string;
    title: string;
    description: string; // Anonymized
    pathways: RegulatoryPathway[];
    outcome: string; // "Approval secured in 10 months"
}

export interface FilterState {
  regions: Region[];
  therapeuticAreas: TherapeuticArea[];
  services: ServiceType[];
  pathways: RegulatoryPathway[];
  productTypes: ProductType[];
  searchQuery: string;
  verifiedOnly: boolean;
}

export interface MockAnalyticsData {
  name: string;
  value: number;
}

export interface Proposal {
  id: string;
  rfqId: string;
  consultancyId: string;
  consultancyName: string;
  price: number;
  currency: string;
  timelineWeeks: number;
  status: 'pending' | 'shortlisted' | 'rejected' | 'awarded';
  submittedDate: string;
  summary: string;
}

export interface ShortlistEntry {
  id: string;
  consultancyId: string;
  note: string;
  addedDate: string;
}

export interface MatchResult {
    consultancyId: string;
    score: number; // 0-100
    reasons: string[]; // e.g., "Strong Orphan Drug experience"
}

export interface RFQ {
  id: string;
  title: string;
  description: string;
  status: 'draft' | 'open' | 'evaluation' | 'awarded' | 'closed' | 'archived';
  regions: Region[];
  therapeuticArea: TherapeuticArea;
  pathway?: RegulatoryPathway;
  budgetRange?: string;
  postedDate: string;
  deadline?: string;
  targetStart?: string;
  proposalCount: number;
  
  // Automation & SLA fields
  invitedConsultancyIds?: string[];
  matchScores?: Record<string, number>; // consultancyId -> score
  autoCloseDate?: string;
  slaMetrics?: {
      timeToFirstView?: number; // hours
      avgResponseTime?: number; // hours
      responseRate?: number; // percentage
  };
}

export interface VerificationRequest {
    id: string;
    consultancyId: string;
    consultancyName: string;
    claimType: 'Approval' | 'Designation' | 'Meeting';
    details: string; // e.g. "FDA Orphan Designation for molecule X"
    evidenceFiles: Evidence[];
    status: 'pending' | 'approved' | 'rejected' | 'more_info_needed';
    submittedDate: string;
}

export interface Evidence {
    id: string;
    name: string;
    url: string;
    type: 'pdf' | 'image';
}

export interface ClientFeedback {
    id: string;
    projectId: string;
    consultancyId: string;
    rating: number; // 1-5
    comment: string;
    isVerifiedPurchase: boolean;
    date: string;
}

export interface Subscription {
    planId: 'foundation' | 'professional' | 'enterprise';
    status: 'active' | 'past_due' | 'canceled';
    nextBillingDate: string;
    paymentMethodLast4: string;
    features: {
        rfqsRemaining: number;
        canViewAnalytics: boolean;
        canExportData: boolean;
    };
}

export interface Invoice {
    id: string;
    date: string;
    amount: number;
    status: 'paid' | 'open' | 'void';
    pdfUrl: string;
}

export interface PaymentMethod {
    id: string;
    type: 'card';
    last4: string;
    expiry: string;
    brand: 'visa' | 'mastercard' | 'amex';
}

export interface AddOn {
    id: string;
    name: string;
    price: number;
    description: string;
    purchased: boolean;
}

export interface Notification {
    id: string;
    type: 'system' | 'rfq' | 'message' | 'billing';
    title: string;
    message: string;
    date: string;
    read: boolean;
    link?: string;
}

export interface Message {
    id: string;
    senderId: string;
    senderName: string;
    content: string;
    timestamp: Date;
    read: boolean;
    attachments?: {name: string, url: string}[];
}

export interface Thread {
    id: string; // usually RFQ ID or Project ID
    subject: string;
    participants: {id: string, name: string}[];
    lastMessage: Message;
    unreadCount: number;
}

export interface Task {
    id: string;
    title: string;
    dueDate: string; // ISO Date
    completed: boolean;
    priority: 'high' | 'medium' | 'low';
}

export interface ReportedIssue {
    id: string;
    targetId: string; // User or Consultancy ID
    targetName: string;
    reporter: string;
    reason: string; // e.g. "Misrepresented track record"
    status: 'open' | 'resolved' | 'dismissed';
    date: string;
    severity: 'low' | 'medium' | 'high';
}

export interface ContentItem {
    id: string;
    title: string;
    type: 'guide' | 'webinar' | 'blog';
    status: 'draft' | 'published' | 'archived';
    views: number;
    lastUpdated: string;
    author: string;
}

export interface PlatformConfig {
    pricing: {
        foundation: number;
        professional: number;
        enterprise: number;
    };
    trialDays: number;
    features: Record<string, boolean>;
}

// --- Security & Settings ---

export interface ActivityLog {
    id: string;
    action: string;
    user: string;
    timestamp: string;
    ipAddress: string;
    details?: string;
}

export interface OrgSettings {
    allowedDomains: string[];
    require2FA: boolean;
    visibility: 'public' | 'private' | 'verified_only';
    defaultNDALink?: string;
    notifications: {
        emailRfqUpdates: boolean;
        emailMarketing: boolean;
        emailSecurityAlerts: boolean;
    };
}
